import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * One place where every term the tool prints is defined in plain words.
 *
 * The scores were readable and the vocabulary was not: "cited" in particular
 * looked like a second number to grow, when it is the name of a specific loss.
 * A term the reader has to guess at is worse than no term, so each one now
 * carries an ⓘ that says what it measures, what a good value looks like, and —
 * the part a definition usually omits — what you would actually do about it.
 */
export interface Term {
  id: string;
  label: string;
  /** One line: what the number literally counts. */
  what: string;
  /** What it means for the site, in the client's terms. */
  means: string;
  /** The action it implies. Omitted where there honestly isn't one. */
  todo?: string;
  /** Where the number comes from — measured, sampled or derived. Honesty rule. */
  howKnown?: string;
}

export const TERMS: Record<string, Term> = {
  mentioned: {
    id: 'mentioned',
    label: 'Named (mentioned)',
    what: 'The AI wrote your brand name in the body of its answer.',
    means: 'This is the win, and the only state that counts as one. Someone asking this question is told about you.',
    todo: 'Protect it, and re-check it over time. An answer you win today is not permanent, because the model re-retrieves every time it is asked.',
    howKnown: 'Measured: your brand name (and its aliases) matched in the answer text.',
  },
  cited_only: {
    id: 'cited_only',
    label: 'Used your page, didn’t name you',
    what: 'Your page appeared in the AI’s list of sources, but your brand name never appeared in the answer.',
    means:
      'The AI found you and read you, then wrote the answer without saying who you are. Half the chain already works, which makes this the cheapest problem in the report to fix. It is a LOSS, not a lesser win: you are not a number to grow.',
    todo:
      'Fix it on the page. Put the brand in the sentence that answers the question, not only in the header and footer. Drop the generic phrasing that reads like anyone’s advice, and add Organization schema so the name attaches to the page.',
    howKnown: 'Measured: the URL came back in the answer’s citation list, and the name did not match in the text.',
  },
  absent: {
    id: 'absent',
    label: 'Not named',
    what: 'Neither your name nor your pages appeared anywhere in the answer.',
    means: 'The AI never reached you. This is an off-site problem rather than a page problem, so nothing you change on this page will fix it directly.',
    todo:
      'Get named where the AI does look: the sources listed under “The pages that beat you”, industry directories, marketplaces, review sites. Expect weeks, not days.',
    howKnown: 'Measured, with the caveat that an answer is sampled. See “stable”.',
  },
  share_of_voice: {
    id: 'share_of_voice',
    label: 'Share of voice',
    what: 'How often you are named against the brand named most often across the same questions.',
    means:
      'The number that gives “4 of 25” a meaning. Four is excellent if the leader has five and dismal if it has twenty. This is the figure to watch over time.',
    todo: 'Compare it run to run, on the same question set. Moving it means winning questions the leader currently owns.',
    howKnown: 'Derived from the same answers, and every brand named in them is counted.',
  },
  readiness: {
    id: 'readiness',
    label: 'Readiness',
    what: 'A 0–100 score for how easily an AI can read, understand and quote this page.',
    means:
      'Structure, not popularity: headings, structured data, answerable passages, whether the AI crawlers are allowed in at all. A high score does not mean AI names you. It means nothing on the page is stopping it.',
    todo: 'Work the findings list. These are the fixes fully under your control, and the only ones that take days rather than months.',
    howKnown: 'Measured from the page itself, plus an LLM reading test on the entry page.',
  },
  footprint: {
    id: 'footprint',
    label: 'Footprint (knows you)',
    what: 'Whether the AI can describe your brand from memory when asked directly, with no question about your market attached.',
    means:
      'Visibility asks “who does X?” and sees whether you come up. Footprint asks “what is <your brand>?” and sees whether the model has anything to say. A brand can be well known and still lose every market question, or the reverse. They are different problems, so they are measured separately.',
    todo:
      'If it does not know you: make the facts about your company findable and consistent. Wikidata, your own about page, directories, anywhere the same description repeats.',
    howKnown: 'Measured with a direct question, sampled like the rest.',
  },
  cited: {
    id: 'cited',
    label: 'Cited',
    what: 'Your domain appeared in the source list the AI returned for a question.',
    means: 'Proof that the AI actually fetched your site for that question rather than answering from memory.',
    todo: 'Useful as a diagnosis alongside naming. Cited but not named is a page problem; neither is a reach problem.',
    howKnown: 'Measured: the search tool returns the URLs it used, and your domain is matched against them.',
  },
  stable: {
    id: 'stable',
    label: 'Stable',
    what: 'The same result came back in a clear majority of the samples taken for that question.',
    means:
      'AI answers are not deterministic. Ask twice, get two answers. An unstable result is a coin flip reported as one, rather than a reading you can act on.',
    todo: 'Treat unstable questions as “too close to call” and watch them over several runs before concluding anything.',
    howKnown: 'Each question is asked more than once; this reports the agreement between those runs.',
  },
  question_set: {
    id: 'question_set',
    label: 'Question set',
    what: 'The named list of questions a run asked.',
    means:
      'The trend is only meaningful across runs that asked the same things. Change the set and the line before and after it are two different measurements, which is why the chart marks that point and every run records the set it used.',
    todo: 'Keep one set per angle you care about, and edit it only when your market actually changes.',
  },
  engine: {
    id: 'engine',
    label: 'Engine',
    what: 'Which AI answered, Gemini or GPT.',
    means:
      'They disagree more than people expect, on the same site and the same day. That is the models, not your site. One engine alone is a weak reading.',
    todo: 'Run both on the same set before drawing a conclusion.',
  },
};

/** ⓘ next to a value. Click opens the definition; Esc or a click outside closes it. */
export default function Info({ term, className = '' }: { term: keyof typeof TERMS | string; className?: string }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  /** The portalled panel, so a click inside it is not "outside". */
  const panel = useRef<HTMLSpanElement>(null);
  const t = TERMS[term];

  /**
   * Where to draw the popover, in viewport coordinates.
   *
   * It used to be absolutely positioned inside the trigger. That works until an
   * ancestor has `overflow-x: auto` — which the run table does, because it is
   * wide and has to scroll — and an auto overflow clips on BOTH axes. The
   * definition was then cut off at the table's edge, which is precisely where
   * these headers live.
   *
   * A portal takes it out of the scroller entirely. The cost is positioning it
   * by hand, and the flip below, which the browser would otherwise have done.
   */
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !box.current) { setPos(null); return; }
    const place = (): void => {
      const r = box.current?.getBoundingClientRect();
      if (!r) return;
      const W = 320;                      // matches w-80
      const margin = 12;
      // Flip left when there is not room to the right, so the panel never hangs
      // off the viewport on a narrow window.
      const left = Math.min(Math.max(margin, r.left), window.innerWidth - W - margin);
      setPos({ top: r.bottom + 8, left });
    };
    place();
    // Re-place rather than re-render on scroll: the trigger can be inside a
    // horizontal scroller, so its position changes without React knowing.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent): void => {
      // The panel is portalled to <body>, so it is NOT inside `box` any more and
      // a containment test on the trigger alone would close it the moment you
      // clicked the close button or selected text in it.
      const target = e.target as Node;
      if (box.current?.contains(target)) return;
      if (panel.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [open]);

  if (!t) return null;

  return (
    <span ref={box} className={`relative inline-flex align-middle print:hidden ${className}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label={`what does “${t.label}” mean?`}
        aria-expanded={open}
        className={`w-4 h-4 shrink-0 rounded-full border font-mono text-[10px] leading-none flex items-center justify-center transition ${
          open ? 'border-cyan text-cyan bg-cyan/10' : 'border-[#70707D] text-text-secondary hover:border-cyan hover:text-cyan'
        }`}
      >
        i
      </button>
      {open && pos && createPortal(
        <span
          ref={panel}
          role="dialog"
          aria-label={t.label}
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
          className="z-[60] w-80 max-w-[85vw] text-left bg-warm-dark border border-cyan/40 rounded-xl p-4 shadow-2xl shadow-black/60 cursor-default"
        >
          <span className="flex items-start justify-between gap-2 mb-2">
            <span className="font-serif text-base text-text-primary">{t.label}</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="close"
              className="font-mono text-xs text-text-secondary hover:text-cyan shrink-0"
            >
              ✕
            </button>
          </span>
          <span className="block text-sm text-text-primary">{t.what}</span>
          <span className="block text-sm text-text-secondary mt-2">{t.means}</span>
          {t.todo && (
            <span className="block text-sm text-text-secondary mt-2 pt-2 border-t border-warm-dark-2">
              <span className="font-mono text-xs text-cyan">what to do · </span>{t.todo}
            </span>
          )}
          {t.howKnown && (
            <span className="block font-mono text-xs text-text-secondary/70 mt-2">{t.howKnown}</span>
          )}
        </span>,
        document.body,
      )}
    </span>
  );
}
